import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

export type CrumbStatus = "loading" | "ready" | "notfound";

export type CrumbOverride = {
  status: CrumbStatus;
  label: string | null;
};

type Ctx = {
  overrides: Map<number, CrumbOverride>;
  setOverride: (idx: number, override: CrumbOverride) => void;
  clearOverride: (idx: number) => void;
};

const BreadcrumbContext = createContext<Ctx | null>(null);

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [overrides, setOverrides] = useState<Map<number, CrumbOverride>>(
    () => new Map(),
  );

  const setOverride = useCallback((idx: number, override: CrumbOverride) => {
    setOverrides((prev) => {
      const existing = prev.get(idx);
      if (
        existing &&
        existing.status === override.status &&
        existing.label === override.label
      ) {
        return prev;
      }
      const next = new Map(prev);
      next.set(idx, override);
      return next;
    });
  }, []);

  const clearOverride = useCallback((idx: number) => {
    setOverrides((prev) => {
      if (!prev.has(idx)) return prev;
      const next = new Map(prev);
      next.delete(idx);
      return next;
    });
  }, []);

  const value = useMemo<Ctx>(
    () => ({ overrides, setOverride, clearOverride }),
    [overrides, setOverride, clearOverride],
  );

  return (
    <BreadcrumbContext.Provider value={value}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

export function useBreadcrumbContext(): Ctx {
  const ctx = useContext(BreadcrumbContext);
  if (!ctx) throw new Error("useBreadcrumbContext outside provider");
  return ctx;
}

/**
 * Detail pages call this with the resolved entity name.
 * `idx` = zero-based position of the UUID segment in the URL path
 * (e.g. /projects/:id → idx=1, /work-units/:id → idx=1).
 */
export function useBreadcrumb(
  idx: number,
  label: string | null | undefined,
  status: CrumbStatus,
) {
  const { setOverride, clearOverride } = useBreadcrumbContext();
  const idxRef = useRef(idx);
  idxRef.current = idx;

  useEffect(() => {
    setOverride(idx, { status, label: label ?? null });
  }, [idx, label, status, setOverride]);

  useEffect(() => {
    return () => clearOverride(idxRef.current);
  }, [clearOverride]);
}
